export type SearchKind = "person" | "org" | "client" | "app";

export type SearchResult = {
  kind: SearchKind;
  id: string;
  title: string;
  sub: string;
};

export type SearchData = {
  persons?: Array<{ id: string; name: string; email?: string }>;
  orgs?: Array<{ id: string; name: string; city?: string }>;
  clients?: Array<{ clientId: string; clientName: string }>;
  apps?: Array<{ uuid: string; name: string; clientId?: string }>;
};

// Cap EACH kind, not just the total: a term like a school's email domain can
// match dozens of people, which must not crowd the (few) matching orgs/clients
// out of a single flat list. Orgs come first — this is a school-admin console.
const MAX_PER_KIND = 6;
const MAX_RESULTS = 14;

/** Client-side global search over already-loaded entities (ported from data.jsx). */
export function globalSearch(query: string, data: SearchData): SearchResult[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return [];
  }

  const orgs: SearchResult[] = [];
  for (const o of data.orgs ?? []) {
    if (o.name.toLowerCase().includes(q) || (o.city ?? "").toLowerCase().includes(q)) {
      orgs.push({ kind: "org", id: o.id, title: o.name, sub: o.city ?? "" });
    }
  }
  const persons: SearchResult[] = [];
  for (const u of data.persons ?? []) {
    if (u.name.toLowerCase().includes(q) || (u.email ?? "").toLowerCase().includes(q)) {
      persons.push({ kind: "person", id: u.id, title: u.name, sub: u.email ?? "" });
    }
  }
  const clients: SearchResult[] = [];
  for (const c of data.clients ?? []) {
    if (c.clientName.toLowerCase().includes(q) || c.clientId.toLowerCase().includes(q)) {
      clients.push({ kind: "client", id: c.clientId, title: c.clientName, sub: c.clientId });
    }
  }
  const apps: SearchResult[] = [];
  for (const a of data.apps ?? []) {
    if (a.name.toLowerCase().includes(q) || (a.clientId ?? "").toLowerCase().includes(q)) {
      apps.push({ kind: "app", id: a.uuid, title: a.name, sub: a.clientId ?? "" });
    }
  }

  return [
    ...orgs.slice(0, MAX_PER_KIND),
    ...persons.slice(0, MAX_PER_KIND),
    ...clients.slice(0, MAX_PER_KIND),
    ...apps.slice(0, MAX_PER_KIND),
  ].slice(0, MAX_RESULTS);
}
